<template>
  <div>
    <v-card
      color="grey lighten-4"
      rounded="lg"
      class="px-3 py-2 hoverable-card"
    >
      <v-row no-gutters>
        <v-col
          v-if="showMedia"
          cols="auto"
          class="pr-3"
        >
          <img
            v-if="media && media.thumbnail_url"
            :src="media.thumbnail_url"
            style="width: 80px; height: 50px; cursor: pointer"
            @click="mediaDialog = true"
          >
          <img
            v-else-if="media && media.mime_type.includes('image/')"
            :src="media.url"
            style="width: 80px; height: 50px; cursor: pointer"
            @click="mediaDialog = true"
          >
          <video
            v-else-if="media && (media.mime_type.includes('video/') || ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'flv', 'wmv'].indexOf(media.extension) > -1)"
            height="50px"
            width="80px"
            :src="media.url"
            style="cursor: pointer"
            @click="mediaDialog = true"
          />
          <v-img
            v-else
            height="50px"
            width="80px"
          >
            <template v-slot:placeholder>
              <v-row
                no-gutters
                align="center"
                justify="center"
                class="fill-height"
              >
                <v-col cols="auto">
                  <v-icon large>
                    fal fa-dumbbell
                  </v-icon>
                </v-col>
              </v-row>
            </template>
          </v-img>
        </v-col>
        <v-col
          v-if="orderable"
          cols="auto"
          class="pr-3 pt-1"
        >
          <v-icon
            small
            class="handle"
            style="cursor: move"
          >
            far fa-grip-lines
          </v-icon>
        </v-col>
        <v-col>
          <v-row
            no-gutters
            align="center"
            :style="[ (editable || feedbackAllowed) ? { minHeight: '30px' } : undefined ]"
          >
            <v-col>
              <div
                class="font-weight-medium"
                :class="{ 'caption': dense }"
              >
                {{ exercise.name }}
              </div>
            </v-col>
            <v-col
              v-if="feedbackAllowed"
              cols="auto"
            >
              <v-btn
                text
                color="primary"
                small
                outlined
                class="text-none"
                @click="startFeedbackEntry()"
              >
                <v-icon
                  x-small
                  left
                >
                  far fa-list-check
                </v-icon>
                {{ $t("button.feedback") }}
              </v-btn>
            </v-col>
            <v-col
              v-if="editable"
              cols="auto"
              class="show-hover"
            >
              <v-btn
                v-if="editable"
                text
                color="grey darken-2"
                small
                outlined
                class="text-none"
                @click="startParamsEntry()"
              >
                <v-icon
                  x-small
                  left
                >
                  far fa-pen
                </v-icon>
                {{ $t('training_builder.button.parameters') }}
              </v-btn>
              <v-btn
                v-if="editable"
                text
                color="error"
                small
                @click="$emit('delete')"
              >
                <v-icon x-small>
                  far fa-trash
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <div
            v-if="!dense"
            class="caption mb-3"
          >
            {{ exercise.description }}
          </div>

          <exercise-params-view-template
            :params="exercise.params"
            :sets="exercise.params.SETS"
            :group-id="exercise.group.group_id"
          />
        </v-col>
      </v-row>
    </v-card>

    <card-dialog
      v-model="paramsDialog"
      max-width="800px"
    >
      <template v-slot:title>
        <v-card-title
          class="blue-grey lighten-5 px-6 pt-4 pb-0"
        >
          <div
            class="d-flex flex-column"
            style="width: 100%"
          >
            <div>{{ $t('training_builder.configure_parameters.dialog.header') }} {{ exercise.name }}</div>
            <div class="mt-4">
              <v-row
                no-gutters
                align="center"
                class="flex-nowrap"
              >
                <v-col style="min-width: 0">
                  <v-tabs
                    v-model="currentSet"
                    height="36px"
                    background-color="transparent"
                  >
                    <v-tab
                      v-for="(setData, setIndex) in variables.SETS"
                      :key="'tabset'+setIndex"
                      :href="`#${setIndex}`"
                      class="text-none text-body-1 px-2"
                    >
                      {{ $t("training_builder.configure.set", {set: setIndex + 1 }) }}
                    </v-tab>
                    <v-tab
                      class="text-none text-body-1 px-2"
                      href="#total"
                    >
                      {{ $t('training_builder.configure.total') }}
                    </v-tab>
                  </v-tabs>
                </v-col>
                <v-col
                  v-if="!isFeedbackEntry && !forceSets && currentSet !== 'total'"
                  cols="auto"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        text
                        color="primary"
                        class="text-none"
                        small
                        :disabled="!!forceSets"
                        @click="duplicateSet(currentSet)"
                        v-on="on"
                      >
                        <v-icon
                          x-small
                        >
                          far fa-copy
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>
                      {{ $t('training_builder.button.duplicate_set.tooltip') }}
                    </span>
                  </v-tooltip>
                </v-col>
                <v-col
                  v-if="!isFeedbackEntry && !forceSets && currentSet !== 'total'"
                  cols="auto"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        text
                        color="error"
                        class="text-none"
                        :disabled="currentSet === 0"
                        small
                        @click="deleteSet()"
                        v-on="on"
                      >
                        <v-icon
                          x-small
                        >
                          far fa-trash
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>
                      {{ $t('training_builder.button.delete_set.tooltip') }}
                    </span>
                  </v-tooltip>
                </v-col>
                <v-col
                  v-if="!isFeedbackEntry && !forceSets"
                  cols="auto"
                >
                  <v-btn
                    text
                    color="primary"
                    class="text-none"
                    small
                    @click="addSet()"
                  >
                    <v-icon
                      x-small
                      left
                    >
                      far fa-plus
                    </v-icon>
                    {{ $t('training_builder.button.add_set') }}
                  </v-btn>
                </v-col>
                <v-col
                  v-if="!isFeedbackEntry && forceSets && currentSet !== 'total'"
                  cols="auto"
                >
                  <v-btn
                    text
                    color="primary"
                    class="text-none"
                    small
                    @click="copySetToOthers(currentSet)"
                  >
                    <v-icon
                      x-small
                      left
                    >
                      far fa-check
                    </v-icon>
                    Apply to all sets
                  </v-btn>
                </v-col>
                <v-col
                  v-if="historyAllowed"
                  cols="auto"
                >
                  <v-btn
                    text
                    color="grey darken-2"
                    small
                    class="text-none"
                    @click="historyDialog = true"
                  >
                    <v-icon
                      x-small
                      left
                    >
                      far fa-clock-rotate-left
                    </v-icon>
                    {{ $t("button.history") }}
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card-title>
      </template>

      <v-card-text
        class="pa-0"
      >
        <div
          v-if="currentSet === 'total'"
          class="pa-6"
        >
          <validation-observer ref="validationObserver">
            <v-row>
              <v-col cols="6">
                <minute-second-input
                  :value="variables.TEXDR * 60"
                  variable="TEXDR"
                  :label="$t('training_builder.text_input.exercise_duration.title')"
                  @input="handleDurationInput"
                />
              </v-col>
            </v-row>
          </validation-observer>
        </div>
        <div
          v-else-if="variables.SETS && currentSet < variables.SETS.length && variables.SETS[currentSet]"
          class="pa-6"
        >
          <validation-observer ref="validationObserver">
            <ballistics-params-entry
              v-if="exercise.group.group_id === groupIds.BALLISTICS"
              :key="'set' + currentSet"
              :set-params.sync="variables.SETS[currentSet]"
              :exercise="exercise"
            />
            <cardio-params-entry
              v-else-if="exercise.group.group_id === groupIds.CARDIO"
              :key="'set' + currentSet"
              :set-params.sync="variables.SETS[currentSet]"
              :exercise="exercise"
            />
            <endurance-params-entry
              v-else-if="exercise.group.group_id === groupIds.ENDURANCE"
              :key="'set' + currentSet"
              :set-params.sync="variables.SETS[currentSet]"
              :exercise="exercise"
            />
            <football-params-entry
              v-else-if="exercise.group.group_id === groupIds.FOOTBALL"
              :key="'set' + currentSet"
              :set-params.sync="variables.SETS[currentSet]"
              :exercise="exercise"
            />
            <plyometrics-params-entry
              v-else-if="exercise.group.group_id === groupIds.PLYOMETRICS"
              :key="'set' + currentSet"
              :set-params.sync="variables.SETS[currentSet]"
              :exercise="exercise"
            />
            <speed-params-entry
              v-else-if="exercise.group.group_id === groupIds.SPEED"
              :key="'set' + currentSet"
              :set-params.sync="variables.SETS[currentSet]"
              :exercise="exercise"
            />
            <strength-params-entry
              v-else-if="exercise.group.group_id === groupIds.STRENGTH"
              :key="'set' + currentSet"
              :set-params.sync="variables.SETS[currentSet]"
              :exercise="exercise"
            />
            <mobility-params-entry
              v-else-if="exercise.group.group_id === groupIds.MOBILITY"
              :key="'set' + currentSet"
              :set-params.sync="variables.SETS[currentSet]"
              :exercise="exercise"
            />
          </validation-observer>
        </div>
      </v-card-text>

      <v-card-actions class="grey-bg">
        <v-spacer />
        <v-btn
          color="primary"
          @click="save()"
        >
          {{ $t('training_builder.button.save') }}
        </v-btn>
      </v-card-actions>
    </card-dialog>

    <player-exercise-history-dialog
      v-if="player"
      :visible.sync="historyDialog"
      :player="player"
      :group-id="exercise.group.group_id"
      :training-exercise="exercise"
    />

    <media-viewer-dialog
      v-if="media"
      :visible.sync="mediaDialog"
      :media="[ media ]"
      :view-media-id="media.id"
      view-only
      :can-download-video="false"
    />
  </div>
</template>
<script>
import CardDialog from '@/components/helpers/CardDialog.vue'
import { cloneVariables } from '@/components/training-builder/helpers'
import PlayerExerciseHistoryDialog from '@/components/training-builder/PlayerExerciseHistoryDialog.vue'
import { TrainingExerciseGroupId, TrainingExercisesGroups } from '@/data/models/TrainingExercise'
import StrengthParamsEntry from '@/components/training-builder/strength/StrengthParamsEntry.vue'
import CardioParamsEntry from '@/components/training-builder/cardio/CardioParamsEntry.vue'
import BallisticsParamsEntry from '@/components/training-builder/ballistics/BallisticsParamsEntry.vue'
import EnduranceParamsEntry from '@/components/training-builder/endurance/EnduranceParamsEntry.vue'
import FootballParamsEntry from '@/components/training-builder/football/FootballParamsEntry.vue'
import PlyometricsParamsEntry from '@/components/training-builder/plyometrics/PlyometricsParamsEntry.vue'
import SpeedParamsEntry from '@/components/training-builder/speed/SpeedParamsEntry.vue'
import ExerciseParamsViewTemplate from '@/components/training-builder/ExerciseParamsViewTemplate.vue'
import MediaViewerDialog from '@/components/dialogs/MediaViewerDialog.vue'
import MobilityParamsEntry from '@/components/training-builder/mobility/MobilityParamsEntry.vue'
import MinuteSecondInput from '@/components/helpers/MinuteSecondInput.vue'

export default {
  name: 'ExerciseSelectedCardTemplate',
  components: { MinuteSecondInput, MobilityParamsEntry, MediaViewerDialog, ExerciseParamsViewTemplate, SpeedParamsEntry, PlyometricsParamsEntry, FootballParamsEntry, EnduranceParamsEntry, BallisticsParamsEntry, CardioParamsEntry, StrengthParamsEntry, PlayerExerciseHistoryDialog, CardDialog },
  props: {
    exercise: {
      type: Object,
      required: true
    },
    dense: {
      type: Boolean,
      default: false
    },
    orderable: {
      type: Boolean,
      default: true
    },
    editable: {
      type: Boolean,
      default: true
    },
    feedbackAllowed: {
      type: Boolean,
      default: false
    },
    showMedia: {
      type: Boolean,
      default: false
    },
    forceSets: {
      type: Number,
      default: undefined
    },
    historyAllowed: {
      type: Boolean,
      default: false
    },
    player: {
      type: Object,
      default: () => undefined
    }
  },
  data() {
    return {
      paramsDialog: false,
      isFeedbackEntry: false,
      currentSet: 0,
      variables: {
        TEXDR: null,
        SETS: [
          cloneVariables(TrainingExercisesGroups[this.exercise.group.group_id].params)
        ]
      },
      mediaDialog: false,
      historyDialog: false
    }
  },

  computed: {
    media() {
      if (!this.exercise.media || this.exercise.media.length === 0) return undefined

      return this.exercise.media[0]
    }
  },

  watch: {
    forceSets() {
      this.adjustSetCount()
    }
  },

  created() {
    this.groupIds = TrainingExerciseGroupId

    if (!this.exercise.params) {
      this.exercise.params = cloneVariables(this.variables)
    } else if (this.exercise.params) {
      this.variables = cloneVariables(this.exercise.params)
    }

    if (this.exercise.configureParams) {
      this.startParamsEntry()
      this.exercise.configureParams = false
    }

    this.adjustSetCount()
  },

  methods: {
    async save() {
      const result = await this.$refs.validationObserver.validate()

      if (!result) return

      if (!this.isFeedbackEntry) {
        this.exercise.params = cloneVariables(this.variables)
      } else {
        this.exercise.realization_params = cloneVariables(this.variables)

        await this.$nextTick()

        this.$emit('feedback')
      }

      this.isFeedbackEntry = false
      this.paramsDialog = false
    },
    addSet() {
      this.variables.SETS.push(cloneVariables(TrainingExercisesGroups[this.exercise.group.group_id].params))
    },
    duplicateSet(setNumber) {
      if (!this.variables.SETS[setNumber]) return

      this.variables.SETS.push({ ...this.variables.SETS[setNumber] })
    },
    copySetToOthers(setNumber) {
      if (!this.variables.SETS[setNumber]) return

      const set = this.variables.SETS[setNumber]

      for (let i = 0; i < this.variables.SETS.length; i++) {
        if (i === setNumber) continue

        this.variables.SETS[i] = { ...set }
      }
    },
    deleteSet() {
      this.variables.SETS.splice(this.currentSet, 1)
    },
    async adjustSetCount() {
      await this.$nextTick()

      if (!this.forceSets) return

      const setCount = this.variables.SETS.length

      const set = cloneVariables(this.variables.SETS[0] || TrainingExercisesGroups[this.exercise.group.group_id].params)

      if (setCount < this.forceSets) {
        this.variables.SETS.push(...(Array(this.forceSets - setCount).fill(set)))

        this.exercise.params = cloneVariables(this.variables)
      } else if (setCount > this.forceSets) {
        this.variables.SETS = this.variables.SETS.slice(0, this.forceSets)

        this.exercise.params = cloneVariables(this.variables)
      }
    },
    startParamsEntry() {
      if (this.exercise.params) {
        this.variables = cloneVariables(this.exercise.params)
      }

      this.isFeedbackEntry = false
      this.paramsDialog = true
    },
    startFeedbackEntry() {
      if (this.exercise.realization_params) {
        this.variables = cloneVariables(this.exercise.realization_params)
      } else {
        this.variables = cloneVariables(this.exercise.params)
      }

      this.isFeedbackEntry = true
      this.paramsDialog = true
    },
    handleDurationInput(min, sec, variable) {
      this.variables[variable] = min + Math.round(sec * 100 / 60) / 100
    }
  }
}
</script>

<style lang="scss">
.hoverable-card {
  & > * > * > * > .show-hover {
    display: none;
  }

  & > * > .show-hover {
    display: none;
  }

  &:hover {
    & > * > * > * >.show-hover {
      display: revert;
    }

    & > * > .show-hover {
      display: revert;
    }
  }
}
</style>
